import { useState } from 'react';

interface MetadataViewerProps {
  metadata: Record<string, any>;
}

export function MetadataViewer({ metadata }: MetadataViewerProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['conversation']));

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Extract structured data
  const messageType = metadata.message_type || 'unknown';
  const aiGenerated = metadata.ai_generated;
  const rawMessages = metadata.raw_messages || [];
  
  const requestMessage = rawMessages.find((m: any) => m.kind === 'request');
  const responseMessage = rawMessages.find((m: any) => m.kind === 'response');

  return (
    <div className="space-y-4">
      {/* Header with badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {aiGenerated && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
            </svg>
            AI Generated
          </span>
        )}
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700">
          {messageType.replace('_', ' ')}
        </span>
      </div>

      {/* Conversation Flow */}
      {(requestMessage || responseMessage) && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('conversation')}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
          >
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
              Conversation Flow
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.has('conversation') ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.has('conversation') && (
            <div className="p-4 space-y-3">
              {/* Request */}
              {requestMessage && requestMessage.parts && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Request
                    </span>
                    <button
                      onClick={() => copyToClipboard(requestMessage.parts[0]?.content || '')}
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                      {requestMessage.parts[0]?.content || 'No content'}
                    </p>
                    {requestMessage.parts[0]?.timestamp && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {formatTimestamp(requestMessage.parts[0].timestamp)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Arrow */}
              {requestMessage && responseMessage && (
                <div className="flex justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              )}

              {/* Response */}
              {responseMessage && responseMessage.parts && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Response
                    </span>
                    <button
                      onClick={() => copyToClipboard(responseMessage.parts[0]?.content || '')}
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                      {responseMessage.parts[0]?.content || 'No content'}
                    </p>
                    {responseMessage.timestamp && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {formatTimestamp(responseMessage.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Token Usage */}
      {responseMessage?.usage && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('tokens')}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
          >
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
              Token Usage
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.has('tokens') ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.has('tokens') && (
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                  <div className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">
                    Input
                  </div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    {formatNumber(responseMessage.usage.input_tokens)}
                  </div>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                  <div className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                    Output
                  </div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {formatNumber(responseMessage.usage.output_tokens)}
                  </div>
                </div>

                {responseMessage.usage.cache_read_tokens > 0 && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                    <div className="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-1">
                      Cache Read
                    </div>
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                      {formatNumber(responseMessage.usage.cache_read_tokens)}
                    </div>
                  </div>
                )}

                {responseMessage.usage.cache_write_tokens > 0 && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                    <div className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide mb-1">
                      Cache Write
                    </div>
                    <div className="text-2xl font-bold text-orange-700 dark:text-orange-300">
                      {formatNumber(responseMessage.usage.cache_write_tokens)}
                    </div>
                  </div>
                )}
              </div>

              {/* Details Section */}
              {responseMessage.usage.details && (
                <details className="mt-3">
                  <summary className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200">
                    View detailed breakdown
                  </summary>
                  <div className="mt-2 text-xs space-y-1 text-gray-600 dark:text-gray-400">
                    {Object.entries(responseMessage.usage.details).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-mono">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {/* Model Info */}
      {responseMessage && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('model')}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
          >
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
              Model Information
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections.has('model') ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.has('model') && (
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 gap-2 text-sm">
                {responseMessage.model_name && (
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Model</span>
                    <span className="font-mono text-gray-900 dark:text-gray-100">
                      {responseMessage.model_name}
                    </span>
                  </div>
                )}
                
                {responseMessage.provider_name && (
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Provider</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100 capitalize">
                      {responseMessage.provider_name}
                    </span>
                  </div>
                )}
                
                {responseMessage.provider_response_id && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 dark:text-gray-400">Response ID</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-gray-900 dark:text-gray-100">
                        {responseMessage.provider_response_id}
                      </span>
                      <button
                        onClick={() => copyToClipboard(responseMessage.provider_response_id)}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Raw JSON fallback (collapsed by default) */}
      <details className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <summary className="px-4 py-3 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors text-sm font-medium text-gray-900 dark:text-gray-100">
          Raw JSON
        </summary>
        <div className="p-4 bg-gray-50 dark:bg-gray-900">
          <div className="flex justify-end mb-2">
            <button
              onClick={() => copyToClipboard(JSON.stringify(metadata, null, 2))}
              className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Copy all
            </button>
          </div>
          <pre className="whitespace-pre-wrap break-words text-xs leading-5 text-gray-800 dark:text-gray-200 overflow-auto max-h-64">
{JSON.stringify(metadata, null, 2)}
          </pre>
        </div>
      </details>
    </div>
  );
}

